import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import toast from 'react-hot-toast';

export default function GemCard({ gem, idx }) {
  const [hovered, setHovered] = useState(false);
  const { addToCart } = useCart();

  const handleAddToCart = e => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(gem);
    toast.success(`${gem.name} added to cart`);
  };

  const img = gem.primary_image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=600&q=80';

  return (
    <Link to={`/gems/${gem.slug}`} style={{ textDecoration: 'none', display: 'flex', height: '100%' }}>
      <article
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: 'var(--surface)',
          border: `1px solid ${hovered ? 'rgba(212,176,106,0.35)' : 'var(--border)'}`,
          borderRadius: 4,
          overflow: 'hidden',
          transition: 'all 0.4s cubic-bezier(0.16,1,0.3,1)',
          transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
          boxShadow: hovered ? '0 24px 64px rgba(0,0,0,0.55)' : 'none',
          cursor: 'pointer',
          display: 'flex', flexDirection: 'column', width: '100%',
        }}
      >
        {/* Image — 4:5 aspect ratio */}
        <div style={{ position: 'relative', aspectRatio: '4/5', overflow: 'hidden', background: 'var(--surface2)' }}>
          <img
            src={img}
            alt={gem.name}
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.7s cubic-bezier(0.16,1,0.3,1)',
              transform: hovered ? 'scale(1.07)' : 'scale(1)',
            }}
            onError={e => { e.target.src = 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=600&q=80'; }}
          />

          {/* Refraction sweep on hover */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0) 100%)',
            transform: hovered ? 'translateX(100%)' : 'translateX(-100%)',
            transition: 'transform 0.7s cubic-bezier(0.16,1,0.3,1)',
            pointerEvents: 'none',
          }} />

          {/* Bottom overlay with Add to Cart */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 55%)',
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.35s ease',
            display: 'flex', alignItems: 'flex-end', padding: 14,
          }}>
            <button onClick={handleAddToCart} className="btn btn-gold btn-sm" style={{ width: '100%', justifyContent: 'center' }}>
              Add to Cart
            </button>
          </div>

          {/* Top-left badges */}
          <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
            {gem.tag && <span className="badge badge-gold">{gem.tag}</span>}
            {gem.certified && (
              <span style={{ background: 'transparent', border: '1px solid rgba(212,176,106,0.6)', color: 'var(--gold)', padding: '2px 7px', fontSize: 8.5, letterSpacing: 1.5, borderRadius: 2, fontFamily: 'var(--font-sans)', fontWeight: 500 }}>
                CERTIFIED
              </span>
            )}
          </div>

          {/* Top-right: index number (optional) */}
          {idx != null && (
            <div style={{ position: 'absolute', top: 10, right: 10, fontFamily: 'var(--font-serif)', fontSize: 11, color: 'rgba(246,241,231,0.35)', letterSpacing: 1 }}>
              {String(idx + 1).padStart(2, '0')}
            </div>
          )}

          {/* Stock warning */}
          {gem.stock_qty <= 2 && gem.stock_qty > 0 && (
            <div style={{ position: 'absolute', bottom: hovered ? 52 : 10, right: 10, background: 'rgba(220,50,50,0.85)', color: '#fff', padding: '2px 8px', fontSize: 8.5, letterSpacing: 1, borderRadius: 2, transition: 'bottom 0.35s ease', fontFamily: 'var(--font-sans)' }}>
              ONLY {gem.stock_qty} LEFT
            </div>
          )}
        </div>

        {/* Info */}
        <div style={{ padding: '16px 16px 20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <p style={{ fontSize: 9.5, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 5, fontFamily: 'var(--font-sans)' }}>
            {gem.category_name}
          </p>
          <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 17, fontWeight: 400, color: 'var(--white)', marginBottom: 5, lineHeight: 1.3 }}>
            {gem.name}
          </h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {gem.weight_ct && <span style={{ fontSize: 10.5, color: 'var(--muted)' }}>{gem.weight_ct} ct</span>}
            {gem.color && <span style={{ fontSize: 10.5, color: 'var(--muted)' }}>· {gem.color}</span>}
            {gem.clarity && <span style={{ fontSize: 10.5, color: 'var(--muted)' }}>· {gem.clarity}</span>}
          </div>
          {/* Price pinned to bottom */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 'auto', paddingTop: 14 }}>
            <span style={{ fontFamily: 'var(--font-serif)', fontSize: 20, color: 'var(--gold)', fontWeight: 400, fontVariantNumeric: 'lining-nums tabular-nums' }}>
              ${Number(gem.price_usd).toLocaleString('en-US', { minimumFractionDigits: 0 })}
            </span>
            {gem.compare_price && (
              <span style={{ fontSize: 12, color: 'var(--muted2)', textDecoration: 'line-through' }}>${Number(gem.compare_price).toLocaleString()}</span>
            )}
            <span style={{ fontSize: 9.5, color: 'var(--muted2)', letterSpacing: 1 }}>USD</span>
          </div>
        </div>
      </article>
    </Link>
  );
}
